import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import moment from 'moment';
import {
    addSupportingDocuments,
    updateSupportingDocuments,
    getCustomer
} from 'school-space-client/api';
import { UserContext, S3Services } from 'school-space-client/components';
import withNotifications from 'shared/context/notification/withNotifications';
import UploadSupportingForm from './uploadform';

const PublicLiabilityInsurance = ({
    documents,
    documentTypeConst,
    setGlobalDocuments,
    context: { showNotification }
}) => {
    const user = useContext(UserContext);
    const [progress, setProgress] = useState(0);
    const [document, setDocument] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [updatedExpiryDate, updateExpiryDate] = useState('');
    const [tempDocument, setTempDocument] = useState([]);

    const uploadFiles = async file => {
        const {
            REACT_APP_S3_BUCKET_NAME: bucketName,
            REACT_APP_S3_REGION: region,
            REACT_APP_S3_ACCESS_KEY_ID: accessKeyId,
            REACT_APP_S3_SECRET_ACCESS_KEY: secretAccessKey
        } = process.env;
        const config = {
            bucketName,
            region,
            accessKeyId,
            secretAccessKey
        };
        const s3client = new S3Services(config);
        return s3client.uploadFile(file);
    };

    const addDocument = payload => {
        setProgress(50);
        addSupportingDocuments(user.id, payload)
            .then(data => {
                setProgress(100);
                setDocument(data.name);
                const tempdocuments = {
                    ...documents,
                    riskAssessment: {
                        ...data
                    }
                };
                setGlobalDocuments(tempdocuments);
                setProgress(0);
                showNotification('Document added successfully! 🚀');
                return window.location.reload(true);
            })
            .catch(error => {
                setProgress(0);
                return showNotification('Error adding document. ⛔️', error);
            });
    };

    const editDocument = payload => {
        const payloadObj = payload;
        payloadObj.expiryDate = updatedExpiryDate;
        setProgress(50);
        updateSupportingDocuments(user.id, documents.liabilityInsurance.id, payloadObj)
            .then(async data => {
                setProgress(100);
                setDocument(data.name);
                const tempdocuments = {
                    ...documents,
                    liabilityInsurance: {
                        ...data
                    }
                };
                setGlobalDocuments(tempdocuments);
                setProgress(0);
                showNotification('Document edited successfully! 🚀');
                return window.location.reload(true);
            })
            .catch(error => {
                setProgress(0);
                return showNotification('Error ⛔️', error);
            });
    };

    const refreshDataCustomer = () => {
        getCustomer(user.id)
            .then(data => {
                user.setCustomerDeposits(data.customerDeposits);
                user.setSupportingDocuments(data.supportingDocuments);
            })
            .catch(error => {
                return showNotification('Error ⛔️', error);
            });
    };

    const liabilityInsurance = async formData => {
        setProgress(25);
        if ((formData.tempDocument[0] || []).length !== 0) {
            const resultS3 = await uploadFiles(formData.tempDocument[0]);
            if (resultS3.result.ok) {
                const payload = {
                    customerId: user.id,
                    approvalStatus: false,
                    name: resultS3.key,
                    documentUrl: resultS3.location,
                    documentType: documentTypeConst.liabilityInsurance,
                    expiryDate: formData.expiryDate || moment().format('DD/MM/YYYY')
                };
                if (document.length !== 0) {
                    await editDocument(payload);
                    return refreshDataCustomer();
                }
                await addDocument(payload);
                return refreshDataCustomer();
            }
            setProgress(0);
            return showNotification('Error updating document. ⛔️');
        }
        setProgress(0);
        return showNotification('Error! No file selected. ⛔️');
    };

    useEffect(() => {
        setDocument(documents.liabilityInsurance.name);
        setExpiryDate(documents.liabilityInsurance.expiryDate);
        updateExpiryDate(documents.liabilityInsurance.expiryDate);
    }, [documents.liabilityInsurance.name, documents.liabilityInsurance.expiryDate]);
    return (
        <>
            <Formik
                enableReinitialize
                initialValues={{
                    expiryDate,
                    setExpiryDate,
                    updateExpiryDate,
                    updatedExpiryDate,
                    document,
                    setDocument,
                    tempDocument,
                    setTempDocument,
                    progress,
                    setProgress
                }}
                onSubmit={liabilityInsurance}
                render={({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    setFieldValue
                }) => (
                    <UploadSupportingForm
                        showExpiryDate
                        showExempt={false}
                        title="proof of Public Liability Insurance"
                        expiryDate={values.expiryDate}
                        updatedDate={values.updatedExpiryDate}
                        setExpiryDate={values.setExpiryDate}
                        updateExpiryDate={values.updateExpiryDate}
                        document={values.document}
                        setDocument={values.setDocument}
                        tempDocument={values.tempDocument}
                        setTempDocument={values.setTempDocument}
                        progress={values.progress}
                        setProgress={values.setProgress}
                        errors={errors}
                        touched={touched}
                        submit={handleSubmit}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                    />
                )}
            />
        </>
    );
};

PublicLiabilityInsurance.defaultProps = {
    documents: {
        riskAssessment: {
            id: '',
            customerId: '',
            name: '',
            documentType: '',
            approvalStatus: false,
            expiryDate: '',
            documentUrl: ''
        },
        safeGaurdingPolicy: {
            id: '',
            customerId: '',
            name: '',
            documentType: '',
            approvalStatus: false,
            expiryDate: '',
            documentUrl: ''
        },
        liabilityInsurance: {
            id: '',
            customerId: '',
            name: '',
            documentType: '',
            approvalStatus: false,
            expiryDate: '',
            updatedExpiryDate: '',
            documentUrl: ''
        }
    },
    documentTypeConst: {
        safeGaurdingPolicy: 'safeGaurdingPolicy',
        riskAssessment: 'riskAssessment',
        liabilityInsurance: 'liabilityInsurance'
    }
};

PublicLiabilityInsurance.propTypes = {
    documents: PropTypes.shape({
        riskAssessment: PropTypes.shape({
            id: PropTypes.string,
            customerId: PropTypes.string,
            name: PropTypes.string,
            documentType: PropTypes.string,
            approvalStatus: PropTypes.bool,
            expiryDate: PropTypes.string,
            documentUrl: PropTypes.string
        }),
        safeGaurdingPolicy: PropTypes.shape({
            id: PropTypes.string,
            customerId: PropTypes.string,
            name: PropTypes.string,
            documentType: PropTypes.string,
            approvalStatus: PropTypes.bool,
            expiryDate: PropTypes.string,
            documentUrl: PropTypes.string
        }),
        liabilityInsurance: PropTypes.shape({
            id: PropTypes.string,
            customerId: PropTypes.string,
            name: PropTypes.string,
            documentType: PropTypes.string,
            approvalStatus: PropTypes.bool,
            expiryDate: PropTypes.string,
            updatedExpiryDate: PropTypes.string,
            documentUrl: PropTypes.string
        })
    }),
    documentTypeConst: PropTypes.shape({
        safeGaurdingPolicy: PropTypes.string,
        riskAssessment: PropTypes.string,
        liabilityInsurance: PropTypes.string
    }),
    setGlobalDocuments: PropTypes.func.isRequired,
    context: PropTypes.objectOf(PropTypes.any).isRequired
};

export default withNotifications(PublicLiabilityInsurance);
